"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Consulenza() {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: paragraphRef,
    isVisible: paragraphVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headlineWords = [{
    text: "Esperienza",
    italic: false
  }, {
    text: "e",
    italic: true
  }, {
    text: "rigore",
    italic: false
  }, {
    text: "nella",
    italic: false
  }, {
    text: "tutela",
    italic: false
  }, {
    text: "medico",
    italic: true
  }, {
    text: "legale.",
    italic: false
  }];
  return <section data-section-id="4985" id="consulenza-medico-legale" className="relative w-full bg-background overflow-hidden dark bg-background text-foreground" style={{
    minHeight: "max(100vh, 800px)"
  }}>
      <div className="relative w-full h-full min-h-[800px] lg:h-screen flex flex-col lg:grid lg:grid-cols-100">
        {}
        <div className="relative z-20 lg:col-span-38 flex flex-col justify-center px-6 sm:px-10 lg:pl-12 xl:pl-20 lg:pr-0 pt-24 pb-16 lg:py-20">
          {}
          <div ref={labelRef} className={`mb-10 lg:mb-16 transition-all duration-700 ease-out ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
            <span className="font-sans text-[11px] sm:text-[12px] uppercase text-muted-foreground tracking-[0.2em]">
              § 01 // CONSULENZA SPECIALISTICA
            </span>
          </div>

          {}
          <h1 ref={headlineRef} className="font-default font-light text-foreground leading-[1.05] mb-8 lg:mb-10 relative z-30" style={{
          fontSize: "clamp(3rem, 8vw, 8rem)",
          letterSpacing: "-0.02em"
        }}>
            <span className="block">
              {headlineWords.slice(0, 3).map((word, i) => <span key={i} data-index={i} className={`inline-block mr-[0.25em] transition-all duration-700 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"} ${word.italic ? "italic" : ""}`} style={{
              transitionDelay: `${i * 40}ms`
            }}>
                  {word.text}
                </span>)}
            </span>
            <span className="block">
              {headlineWords.slice(3, 6).map((word, i) => <span key={i + 3} data-index={i + 3} className={`inline-block mr-[0.25em] transition-all duration-700 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"} ${word.italic ? "italic" : ""}`} style={{
              transitionDelay: `${(i + 3) * 40}ms`
            }}>
                  {word.text}
                </span>)}
            </span>
            <span className="block">
              {headlineWords.slice(6).map((word, i) => <span key={i + 6} data-index={i + 6} className={`inline-block mr-[0.25em] transition-all duration-700 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"} ${word.italic ? "italic" : ""}`} style={{
              transitionDelay: `${(i + 6) * 40}ms`
            }}>
                  {word.text}
                </span>)}
            </span>
          </h1>

          {}
          <p ref={paragraphRef} className={`font-default text-[16px] sm:text-[17px] leading-relaxed text-muted-foreground max-w-[380px] mb-10 transition-all duration-700 ease-out ${paragraphVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
          transitionDelay: "320ms"
        }}>
            Supporto professionale in ambito ginecologico e ostetrico, con particolare attenzione alla fisiopatologia della riproduzione e alla consulenza forense peritale.
          </p>

          {}
          <div ref={ctaRef} className={`flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-7 transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
          transitionDelay: "440ms"
        }}>
            <Button asChild size="lg" className="font-default rounded-lg px-7 h-12 bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md">
              <Link to="/ContactsPage">Richiedi Consulenza</Link>
            </Button>
            <Link to="/ForensicGynecologyPage" className="font-default group inline-flex items-center gap-2 text-foreground text-[15px] relative">
              <span className="relative">
                Approfondisci
                <span className="absolute left-0 -bottom-1 h-px w-full bg-foreground origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-500 ease-out" />
                <span className="absolute left-0 -bottom-1 h-px w-full bg-foreground origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out delay-150" />
              </span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {}
        <div className="relative lg:col-span-62 lg:absolute lg:right-0 lg:top-20 lg:bottom-20 lg:w-[62%] w-full h-[60vh] lg:h-auto px-6 sm:px-10 lg:px-0 lg:pr-12 xl:pr-20 pb-16 lg:pb-0">
          <div ref={imageRef} className={`relative w-full h-full overflow-hidden rounded-lg shadow-lg transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/medical_forensic_expertise.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/medical_forensic_expertise.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/medical_forensic_expertise.webp-wvc-sizes" alt="Dott. Mirko Colloca in studio professionale durante analisi di documentazione clinica" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover will-change-transform" style={{
            transform: `translateY(${scrollY * 0.05}px) scale(1.05)`
          }} />
            <div className="absolute inset-0 bg-gradient-to-tr from-foreground/10 via-transparent to-transparent pointer-events-none" />
          </div>
        </div>

        {}
        <div className="hidden lg:flex absolute left-12 xl:left-20 bottom-10 z-30 flex-col items-center gap-3">
          <span className="font-default italic text-foreground text-sm">I</span>
          <span className="w-px h-12 bg-border" />
        </div>

        {}
        <div className="hidden lg:flex absolute right-12 xl:right-20 bottom-10 z-30 flex-col items-center gap-3">
          <span className="font-sans uppercase text-[10px] tracking-[0.3em] text-muted-foreground" style={{
          writingMode: "vertical-rl",
          transform: "rotate(180deg)"
        }}>
            scroll
          </span>
          <span className="w-px h-12 bg-foreground/40 animate-pulse" />
        </div>
      </div>
    </section>;
}